import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import sqfService from '../../services/sqfService';
import sqfAiService from '../../services/sqfAiService';
import { useLanguage, t } from '../../context/LanguageContext';
import PolicyDocumentView from '../../components/PolicyDocumentView';
import SqfNav from '../../components/SqfNav';
import StartVisualInspectionModal from '../../components/StartVisualInspectionModal';
import { useSqfImport } from '../../context/SqfImportContext';

const CATEGORY_LABELS = {
  management:     'Management',
  food_safety:    'Food Safety',
  prerequisites:  'Prerequisites',
  monitoring:     'Monitoring',
  verification:   'Verification',
  documentation:  'Documentation',
  other:          'Other',
};

const CATEGORY_COLORS = {
  management:     'primary',
  food_safety:    'danger',
  prerequisites:  'success',
  monitoring:     'warning',
  verification:   'info',
  documentation:  'secondary',
  other:          'dark',
};

const COVERAGE_CONFIG = {
  covered: { label: 'Covered',   color: 'success' },
  partial: { label: 'Partial',   color: 'warning' },
  gap:     { label: 'Gap',       color: 'danger'  },
};

function coverageColor(clause) {
  if (clause.coverageLinks.some(l => l.coverageStatus === 'covered')) return 'success';
  if (clause.coverageLinks.some(l => l.coverageStatus === 'partial'))  return 'warning';
  if (clause.coverageLinks.some(l => l.coverageStatus === 'gap'))      return 'danger';
  return 'secondary';
}

function coverageLabel(clause, lang) {
  if (clause.coverageLinks.some(l => l.coverageStatus === 'covered')) return t('Covered', 'Cubierto', lang);
  if (clause.coverageLinks.some(l => l.coverageStatus === 'partial'))  return t('Partial', 'Parcial', lang);
  if (clause.coverageLinks.some(l => l.coverageStatus === 'gap'))      return t('Gap', 'Brecha', lang);
  return t('Uncovered', 'Sin cobertura', lang);
}

export default function SqfAuditHubPage() {
  const { lang } = useLanguage();
  const { startImport, status: importStatus, sqfCodePdfUrl: importedPdfUrl, report: importReport, applicabilityStats: importApplicabilityStats } = useSqfImport();
  const fileInputRef    = useRef(null);
  const linkUploadRef   = useRef(null);

  const [hub, setHub]           = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [showVisualInspectionModal, setShowVisualInspectionModal] = useState(false);

  // Filters
  const [filterCat, setFilterCat]           = useState('');
  const [filterCoverage, setFilterCoverage] = useState('');
  const [filterSearch, setFilterSearch]     = useState('');
  const [filterDeprecated, setFilterDeprecated] = useState(''); // '' | 'deprecated' | 'active'
  const [filterApplicability, setFilterApplicability] = useState(''); // '' | 'hide_na' | 'na_only'

  // Pre-import modal (replaces importModeModal — handles mode + facility profile in one step)
  const [pendingFile, setPendingFile]               = useState(null);
  const [preImportModal, setPreImportModal]         = useState(false);
  const [pendingImportMode, setPendingImportMode]   = useState(null); // 'replace' | 'add'
  const [pendingFacilityProfile, setPendingFacilityProfile] = useState('');

  // Import report modal
  const [reportModal, setReportModal] = useState(null);

  // Standalone applicability re-assessment
  const assessStreamRef = useRef(null);
  const [assessStatus, setAssessStatus]   = useState(null); // null | 'running' | 'done' | 'error'
  const [assessMessage, setAssessMessage] = useState('');

  // Edit facility profile modal (standalone, outside of import)
  const [editProfileModal, setEditProfileModal]   = useState(false);
  const [editProfileValue, setEditProfileValue]   = useState('');
  const [editProfileSaving, setEditProfileSaving] = useState(false);

  // AI Clause Chat modal
  const [chatModal, setChatModal]       = useState(false);
  const [chatQuestion, setChatQuestion] = useState('');
  const [chatAnswer, setChatAnswer]     = useState(null);
  const [chatLoading, setChatLoading]   = useState(false);
  const [chatError, setChatError]       = useState('');

  // Manual add clause modal
  const [addClauseModal, setAddClauseModal]   = useState(false);
  const [addClauseForm, setAddClauseForm]     = useState({ clauseRef: '', elementNumber: '', title: '', requirementText: '', category: 'other' });
  const [addClauseSaving, setAddClauseSaving] = useState(false);
  const [addClauseError, setAddClauseError]   = useState('');

  // Clearing all clauses
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Link modal
  const [linkModal, setLinkModal] = useState(null); // { clause }
  const [linkEntityType, setLinkEntityType] = useState('policy');
  const [linkEntityId, setLinkEntityId]     = useState('');
  const [linkStatus, setLinkStatus]         = useState('covered');
  const [linkNotes, setLinkNotes]           = useState('');
  const [linkSaving, setLinkSaving]         = useState(false);
  const [linkMarkingNA, setLinkMarkingNA]   = useState(false);
  const [linkUploading, setLinkUploading]   = useState(false);
  const [linkUploadMsg, setLinkUploadMsg]   = useState('');
  const [linkSearch, setLinkSearch]         = useState('');
  const [linkDropdownOpen, setLinkDropdownOpen] = useState(false);

  // View document modal
  const [viewModal, setViewModal] = useState(null); // null | { loading, name } | { name, type, policy, version, schemaJson }
  const [viewLang, setViewLang]   = useState('en');

  // Toast notification (replaces window.alert)
  const [toast, setToast] = useState(null); // null | { message, variant }
  const showToast = (message, variant = 'danger') => setToast({ message, variant });

  // Clause detail expand
  const [expandedId, setExpandedId] = useState(null);

  // Attachments
  const attachUploadRef = useRef(null);
  const [attachClauseId, setAttachClauseId]     = useState(null);
  const [attachType, setAttachType]             = useState('certificate');
  const [attachUploading, setAttachUploading]   = useState(false);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    sqfService.getHub()
      .then(d => setHub(d))
      .catch(() => { if (!silent) setError(t('Failed to load Audit Hub data.', 'Error al cargar el hub de auditoría.', lang)); })
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-reload hub data when background import finishes; show report modal
  useEffect(() => {
    if (importStatus === 'done') {
      load();
      if (importReport) setReportModal({ ...importReport, applicabilityStats: importApplicabilityStats });
    }
  }, [importStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredClauses = useMemo(() => {
    if (!hub) return [];
    return hub.clauses.filter(c => {
      if (filterCat && c.category !== filterCat) return false;
      if (filterCoverage) {
        const col = coverageColor(c);
        if (filterCoverage === 'covered'   && col !== 'success')   return false;
        if (filterCoverage === 'partial'   && col !== 'warning')   return false;
        if (filterCoverage === 'gap'       && col !== 'danger')    return false;
        if (filterCoverage === 'uncovered' && col !== 'secondary') return false;
      }
      if (filterDeprecated === 'deprecated' && !c.deprecated) return false;
      if (filterDeprecated === 'active'     &&  c.deprecated) return false;
      if (filterApplicability === 'hide_na' && c.applicabilityStatus === 'not_applicable') return false;
      if (filterApplicability === 'na_only' && c.applicabilityStatus !== 'not_applicable') return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        if (!c.clauseRef.toLowerCase().includes(q) &&
            !(c.title || '').toLowerCase().includes(q) &&
            !(c.requirementText || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [hub, filterCat, filterCoverage, filterSearch, filterDeprecated, filterApplicability]);

  const filteredEntityOptions = useMemo(() => {
    if (!hub || linkEntityType === 'manual') return [];
    const opts = linkEntityType === 'policy' ? hub.policies : hub.templates;
    if (!linkSearch.trim()) return opts;
    const q = linkSearch.toLowerCase();
    return opts.filter(e => e.name.toLowerCase().includes(q));
  }, [hub, linkEntityType, linkSearch]);

  // ─── Import SQF Code PDF ────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const needsMode    = hub && hub.stats.total > 0;
    const needsProfile = !hub?.facilityProfile;
    if (needsMode || needsProfile) {
      setPendingFile(file);
      setPendingImportMode(needsMode ? null : 'replace');
      setPendingFacilityProfile(hub?.facilityProfile || '');
      setPreImportModal(true);
    } else {
      startImport(file, 'replace', hub.facilityProfile);
    }
  };

  const handleConfirmImport = () => {
    const mode    = pendingImportMode || 'replace';
    const profile = pendingFacilityProfile.trim() || hub?.facilityProfile || null;
    setPreImportModal(false);
    startImport(pendingFile, mode, profile);
    setPendingFile(null);
    setPendingImportMode(null);
  };

  // ─── Standalone applicability re-assessment ────────────────────────────────
  const runAssessment = async (profile) => {
    if (assessStreamRef.current) return;
    setAssessStatus('running');
    setAssessMessage('Starting applicability assessment…');
    const API_URL = process.env.REACT_APP_API_URL || '';
    try {
      const response = await fetch(`${API_URL}/api/sqf/ai/assess-applicability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ facilityProfile: profile }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setAssessStatus('error');
        setAssessMessage(err.message || 'Assessment failed.');
        return;
      }
      const reader = response.body.getReader();
      assessStreamRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (value) buffer += decoder.decode(value, { stream: !done });
        const parts = buffer.split('\n\n');
        buffer = done ? '' : parts.pop();
        for (const part of parts) {
          const line = part.split('\n').find(l => l.startsWith('data: '));
          if (!line) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'progress') setAssessMessage(data.message);
            else if (data.type === 'done') { setAssessStatus('done'); setAssessMessage(`Assessment complete — ${data.stats?.not_applicable || 0} N/A clause(s) identified.`); load(); }
            else if (data.type === 'error') { setAssessStatus('error'); setAssessMessage(data.message); }
          } catch { /* ignore */ }
        }
        if (done) break;
      }
    } catch (err) {
      setAssessStatus('error');
      setAssessMessage(err.message || 'Assessment failed.');
    } finally {
      assessStreamRef.current = null;
    }
  };

  // ─── Save facility profile (standalone) ────────────────────────────────────
  const handleSaveProfile = async () => {
    setEditProfileSaving(true);
    try {
      await sqfService.updateFacilityProfile(editProfileValue.trim() || null);
      setHub(prev => prev ? { ...prev, facilityProfile: editProfileValue.trim() || null } : prev);
      setEditProfileModal(false);
    } catch { /* ignore */ } finally { setEditProfileSaving(false); }
  };

  // ─── Clause AI Chat ─────────────────────────────────────────────────────────
  const handleChat = async () => {
    if (!chatQuestion.trim() || chatLoading) return;
    setChatLoading(true);
    setChatAnswer(null);
    setChatError('');
    try {
      const { answer } = await sqfService.clauseChat(chatQuestion.trim());
      setChatAnswer(answer);
    } catch (err) {
      setChatError(err.response?.data?.message || err.message || 'Chat failed.');
    } finally {
      setChatLoading(false);
    }
  };

  // ─── Manual add clause ─────────────────────────────────────────────────────
  const handleAddClause = async () => {
    if (!addClauseForm.clauseRef.trim()) return;
    setAddClauseSaving(true);
    setAddClauseError('');
    try {
      await sqfService.createClause(addClauseForm);
      setAddClauseModal(false);
      setAddClauseForm({ clauseRef: '', elementNumber: '', title: '', requirementText: '', category: 'other' });
      load(true);
    } catch (err) {
      setAddClauseError(err.response?.data?.message || t('Failed to create clause.', 'Error al crear cláusula.', lang));
    } finally {
      setAddClauseSaving(false);
    }
  };

  // ─── Clear all clauses ─────────────────────────────────────────────────────
  const handleClearAll = async () => {
    setClearing(true);
    try {
      await sqfService.deleteAllClauses();
      setShowClearConfirm(false);
      load();
    } catch {
      showToast(t('Failed to clear clauses.', 'Error al eliminar cláusulas.', lang));
    } finally {
      setClearing(false);
    }
  };

  // ─── Delete single clause ───────────────────────────────────────────────────
  const handleDeleteClause = async (id) => {
    try {
      await sqfService.deleteClause(id);
      load(true);
    } catch {
      showToast(t('Failed to delete clause.', 'Error al eliminar cláusula.', lang));
    }
  };

  // ─── Clause attachments ────────────────────────────────────────────────────
  const openAttachPicker = (clauseId) => {
    setAttachClauseId(clauseId);
    attachUploadRef.current?.click();
  };

  const handleAttachFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !attachClauseId) return;
    e.target.value = '';
    setAttachUploading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const { uploadUrl, publicUrl } = await fetch(`${API_URL}/api/documents/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ fileName: file.name, contentType: file.type, fileSize: file.size }),
      }).then(r => r.json());

      await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });

      await sqfService.addClauseAttachment(attachClauseId, {
        fileName: file.name,
        fileUrl: publicUrl,
        mimeType: file.type,
        attachmentType: attachType,
      });

      // Optimistically update local hub state
      setHub(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          clauses: prev.clauses.map(c => c.id !== attachClauseId ? c : {
            ...c,
            attachments: [...(c.attachments || []), {
              id: Date.now(), // temp id until next load
              fileName: file.name, fileUrl: publicUrl, mimeType: file.type, attachmentType: attachType,
            }],
          }),
        };
      });
    } catch (err) {
      showToast(t('Upload failed: ', 'Error al subir: ', lang) + (err.message || ''));
    } finally {
      setAttachUploading(false);
      setAttachClauseId(null);
    }
  };

  const handleDeleteAttachment = async (attachmentId, clauseId) => {
    try {
      await sqfService.deleteClauseAttachment(attachmentId);
      setHub(prev => !prev ? prev : {
        ...prev,
        clauses: prev.clauses.map(c => c.id !== clauseId ? c : {
          ...c,
          attachments: (c.attachments || []).filter(a => a.id !== attachmentId),
        }),
      });
    } catch {
      showToast(t('Failed to delete attachment.', 'Error al eliminar adjunto.', lang));
    }
  };

  // ─── Open link modal ────────────────────────────────────────────────────────
  const openLinkModal = (clause) => {
    setLinkModal({ clause });
    setLinkEntityType('policy');
    setLinkEntityId('');
    setLinkSearch('');
    setLinkDropdownOpen(false);
    setLinkStatus('covered');
    setLinkNotes('');
    setLinkUploadMsg('');
  };

  // ─── Save coverage link ─────────────────────────────────────────────────────
  const handleSaveLink = async () => {
    if (linkEntityType !== 'manual' && !linkEntityId) return;
    setLinkSaving(true);
    try {
      await sqfService.saveCoverageLink({
        clauseId:       linkModal.clause.id,
        entityType:     linkEntityType,
        entityId:       linkEntityType !== 'manual' ? Number(linkEntityId) : null,
        coverageStatus: linkStatus,
        notes:          linkNotes || null,
      });
      setLinkModal(null);
      load(true);
    } catch (err) {
      showToast(err.response?.data?.message || t('Failed to save link.', 'Error al guardar enlace.', lang));
    } finally {
      setLinkSaving(false);
    }
  };

  // ─── Remove coverage link ───────────────────────────────────────────────────
  const handleRemoveLink = async (linkId) => {
    try {
      await sqfService.deleteCoverageLink(linkId);
      load(true);
    } catch {
      showToast(t('Failed to remove link.', 'Error al eliminar enlace.', lang));
    }
  };

  // ─── Upload new document from within link modal ─────────────────────────────
  const handleLinkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setLinkUploading(true);
    setLinkUploadMsg('');
    try {
      const { fileUrl, mimeType } = await sqfAiService.uploadFileForAi(file);
      let result;
      if (linkEntityType === 'policy') {
        result = await sqfAiService.importPolicy({ fileUrl, mimeType });
        // Refresh hub so new policy appears in dropdown, then auto-select it
        const updated = await sqfService.getHub();
        setHub(updated);
        setLinkEntityId(String(result.policyId));
        setLinkUploadMsg(t(`Created draft: "${result.policyName}". Publish it to mark coverage as active.`,
          `Borrador creado: "${result.policyName}". Publíquelo para marcar la cobertura como activa.`, lang));
      } else {
        result = await sqfAiService.importChecklist({ fileUrl, mimeType });
        const updated = await sqfService.getHub();
        setHub(updated);
        setLinkEntityId(String(result.templateId));
        setLinkUploadMsg(t(`Created draft: "${result.templateName}". Publish it to mark coverage as active.`,
          `Borrador creado: "${result.templateName}". Publíquelo para marcar la cobertura como activa.`, lang));
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Upload failed.';
      setLinkUploadMsg(`Error: ${msg}`);
    } finally {
      setLinkUploading(false);
    }
  };

  // ─── View document ─────────────────────────────────────────────────────────
  const handleViewDocument = async (entityType, entityId, entityName) => {
    setViewModal({ loading: true, name: entityName });
    setViewLang(lang === 'es' ? 'es' : 'en');
    try {
      if (entityType === 'policy') {
        const policy = await sqfService.getPolicy(entityId);
        const activeVer = policy.Versions?.find(v => v.id === policy.activeVersionId);
        setViewModal({ type: 'policy', policy, version: activeVer || null });
      } else {
        const tmpl = await sqfService.getTemplate(entityId);
        const activeVer = tmpl.Versions?.find(v => v.id === tmpl.activeVersionId);
        setViewModal({
          name: tmpl.name,
          type: 'checklist',
          schemaJson: activeVer?.schemaJson || { sections: [] },
          templateId: tmpl.id,
        });
      }
    } catch {
      setViewModal(null);
      showToast(t('Failed to load document.', 'Error al cargar el documento.', lang));
    }
  };

  if (loading) return (
    <div className="container mt-4 text-center text-muted py-5">
      <div className="spinner-border spinner-border-sm me-2" role="status" />
      {t('Loading Audit Hub…', 'Cargando hub de auditoría…', lang)}
    </div>
  );

  if (error) return (
    <div className="container mt-4">
      <div className="alert alert-danger">{error}</div>
    </div>
  );

  const { stats, sqfCodePdfUrl: hubPdfUrl, facilityProfile } = hub;
  const sqfCodePdfUrl = hubPdfUrl || importedPdfUrl;
  const deprecatedCount = hub.clauses.filter(c => c.deprecated).length;
  const deprecatedWithLinks = hub.clauses.filter(c => c.deprecated && (c.coverageLinks.length > 0 || (c.attachments || []).length > 0)).length;

  return (
    <>
      <SqfNav />
      {/* ─── Toast notification ──────────────────────────────────────────────── */}
      {toast && (
        <div
          style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, minWidth: 280, maxWidth: 400 }}
          className={`alert alert-${toast.variant || 'danger'} alert-dismissible shadow d-flex align-items-start gap-2 mb-0`}
          role="alert"
        >
          <span className="flex-grow-1" style={{ fontSize: 14 }}>{toast.message}</span>
          <button type="button" className="btn-close ms-2 flex-shrink-0" onClick={() => setToast(null)} />
        </div>
      )}
      <div className="container-fluid mt-4 pb-5" style={{ maxWidth: 1200 }}>

      {/* Assessment status banner */}
      {assessStatus === 'running' && (
        <div className="alert alert-info py-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
          <span className="spinner-border spinner-border-sm flex-shrink-0" />
          <span>{assessMessage}</span>
        </div>
      )}
      {assessStatus === 'done' && (
        <div className="alert alert-success py-2 mb-3 d-flex align-items-center gap-2 justify-content-between" style={{ fontSize: 13 }}>
          <span>✅ {assessMessage}</span>
          <button className="btn-close btn-sm" onClick={() => setAssessStatus(null)} />
        </div>
      )}
      {assessStatus === 'error' && (
        <div className="alert alert-danger py-2 mb-3 d-flex align-items-center gap-2 justify-content-between" style={{ fontSize: 13 }}>
          <span>⚠️ {assessMessage}</span>
          <button className="btn-close btn-sm" onClick={() => setAssessStatus(null)} />
        </div>
      )}

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-0">{t('SQF Audit Hub', 'Hub de auditoría SQF', lang)}</h4>
          <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
            <span className="text-muted" style={{ fontSize: 13 }}>
              {t('Map SQF Code clauses to your policies and checklists.', 'Vincule las cláusulas del código SQF a sus políticas y listas.', lang)}
            </span>
            {facilityProfile ? (
              <span
                className="badge bg-primary-subtle text-primary border border-primary-subtle"
                style={{ fontSize: 11, cursor: 'pointer', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={facilityProfile}
                onClick={() => { setEditProfileValue(facilityProfile); setEditProfileModal(true); }}
              >
                🏭 {facilityProfile.length > 40 ? facilityProfile.slice(0, 40) + '…' : facilityProfile}
              </span>
            ) : (
              <button className="btn btn-link p-0 text-muted" style={{ fontSize: 12 }} onClick={() => { setEditProfileValue(''); setEditProfileModal(true); }}>
                + {t('Set facility type', 'Configurar tipo de instalación', lang)}
              </button>
            )}
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <button className="btn btn-outline-primary btn-sm" onClick={() => setShowVisualInspectionModal(true)}>
            📷 {t('Start Visual Inspection', 'Iniciar Inspección Visual', lang)}
          </button>
          {stats.total > 0 && (
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => { setChatQuestion(''); setChatAnswer(null); setChatError(''); setChatModal(true); }}
            >
              🤖 {t('Ask AI', 'Preguntar a IA', lang)}
            </button>
          )}
          {facilityProfile && stats.total > 0 && (
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={assessStatus === 'running'}
              onClick={() => runAssessment(facilityProfile)}
            >
              {assessStatus === 'running'
                ? <><span className="spinner-border spinner-border-sm me-1" />{t('Assessing…', 'Evaluando…', lang)}</>
                : t('Re-assess N/A', 'Re-evaluar N/A', lang)}
            </button>
          )}
          {sqfCodePdfUrl && (
            <a href={sqfCodePdfUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary btn-sm">
              📄 {t('View SQF Code', 'Ver código SQF', lang)}
            </a>
          )}
          {stats.total > 0 && (
            <button className="btn btn-outline-danger btn-sm" onClick={() => setShowClearConfirm(true)}>
              {t('Clear All Clauses', 'Eliminar todas las cláusulas', lang)}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          {/* Hidden input for clause attachments */}
          <input
            ref={attachUploadRef}
            type="file"
            accept="*/*"
            style={{ display: 'none' }}
            onChange={handleAttachFile}
          />
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => { setAddClauseForm({ clauseRef: '', elementNumber: '', title: '', requirementText: '', category: 'other' }); setAddClauseError(''); setAddClauseModal(true); }}
          >
            + {t('Add Clause', 'Agregar cláusula', lang)}
          </button>
          <button
            className="btn btn-primary btn-sm"
            disabled={importStatus === 'uploading' || importStatus === 'processing'}
            onClick={() => fileInputRef.current?.click()}
          >
            {(importStatus === 'uploading' || importStatus === 'processing')
              ? <><span className="spinner-border spinner-border-sm me-1" />
                  {t('Importing…', 'Importando…', lang)}</>
              : t('Import SQF Code PDF', 'Importar PDF del código SQF', lang)}
          </button>
        </div>
      </div>

      {/* Stats bar */}
      {stats.total > 0 && (
        <div className="row g-2 mb-3">
          {[
            { label: t('Total Clauses', 'Total cláusulas', lang), value: stats.total,          color: 'secondary' },
            { label: t('Covered',       'Cubierto',        lang), value: stats.covered,        color: 'success'   },
            { label: t('Partial',       'Parcial',         lang), value: stats.partial,        color: 'warning'   },
            { label: t('Gap',           'Brecha',          lang), value: stats.gap,            color: 'danger'    },
            { label: t('Uncovered',     'Sin cobertura',   lang), value: stats.uncovered,      color: 'light'     },
            ...(stats.notApplicable > 0 ? [{ label: t('N/A',  'N/A', lang), value: stats.notApplicable, color: 'dark' }] : []),
          ].map(s => (
            <div key={s.label} className="col">
              <div className={`card text-center border-0 shadow-sm bg-${s.color === 'light' ? 'white border' : s.color + '-subtle'}`}>
                <div className="card-body py-2 px-2">
                  <div style={{ fontSize: 24, fontWeight: 700 }}>{s.value}</div>
                  <div className="text-muted" style={{ fontSize: 11 }}>{s.label}</div>
                </div>
              </div>
            </div>
          ))}
          <div className="col-12 mt-1">
            <div className="progress" style={{ height: 8 }}>
              {stats.total > 0 && <>
                <div className="progress-bar bg-success" style={{ width: `${(stats.covered / stats.total) * 100}%` }} />
                <div className="progress-bar bg-warning" style={{ width: `${(stats.partial / stats.total) * 100}%` }} />
                <div className="progress-bar bg-danger" style={{ width: `${(stats.gap / stats.total) * 100}%` }} />
              </>}
            </div>
          </div>
        </div>
      )}

      {/* Deprecated clauses warning banner */}
      {deprecatedWithLinks > 0 && (
        <div className="alert alert-warning d-flex align-items-center gap-3 mb-3 flex-wrap">
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div className="flex-grow-1">
            <strong>{t('Action required:', 'Acción requerida:', lang)}</strong>{' '}
            {t(
              `${deprecatedWithLinks} clause${deprecatedWithLinks > 1 ? 's were' : ' was'} removed in the latest SQF Code import but still have policies, checklists, or files linked to them. Review and re-link to the new clauses.`,
              `${deprecatedWithLinks} cláusula${deprecatedWithLinks > 1 ? 's fueron eliminadas' : ' fue eliminada'} en la última importación pero aún tienen políticas, listas o archivos vinculados. Revise y vuelva a vincular.`,
              lang
            )}
          </div>
          <button
            className="btn btn-warning btn-sm"
            onClick={() => setFilterDeprecated('deprecated')}
          >
            {t('Show deprecated', 'Ver obsoletas', lang)}
          </button>
        </div>
      )}
      {deprecatedCount > 0 && deprecatedWithLinks === 0 && (
        <div className="alert alert-secondary d-flex align-items-center gap-2 mb-3 py-2" style={{ fontSize: 13 }}>
          <span>ℹ️</span>
          <span>
            {t(
              `${deprecatedCount} clause${deprecatedCount > 1 ? 's' : ''} from a previous SQF version ${deprecatedCount > 1 ? 'are' : 'is'} no longer in the current code. No links or attachments are affected.`,
              `${deprecatedCount} cláusula${deprecatedCount > 1 ? 's de una' : ' de una'} versión anterior ya no están en el código actual. No hay vínculos ni adjuntos afectados.`,
              lang
            )}
          </span>
        </div>
      )}

      {/* Empty state */}
      {stats.total === 0 && (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5 text-muted">
            <div style={{ fontSize: 40 }}>📋</div>
            <h5 className="mt-3">{t('No SQF Code clauses imported yet', 'No hay cláusulas del código SQF importadas aún', lang)}</h5>
            <p style={{ fontSize: 14 }}>
              {t('Upload your SQF Code PDF to extract and map all clauses to your policies and checklists.',
                 'Suba su PDF del código SQF para extraer y mapear todas las cláusulas a sus políticas y listas.', lang)}
            </p>
            <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()}>
              {t('Import SQF Code PDF', 'Importar PDF del código SQF', lang)}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      {stats.total > 0 && (
        <div className="d-flex gap-2 flex-wrap mb-3">
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ maxWidth: 240 }}
            placeholder={t('Search clauses…', 'Buscar cláusulas…', lang)}
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
          />
          <select className="form-select form-select-sm" style={{ maxWidth: 180 }}
            value={filterCat} onChange={e => setFilterCat(e.target.value)}>
            <option value="">{t('All Categories', 'Todas las categorías', lang)}</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select className="form-select form-select-sm" style={{ maxWidth: 180 }}
            value={filterCoverage} onChange={e => setFilterCoverage(e.target.value)}>
            <option value="">{t('All Coverage', 'Toda cobertura', lang)}</option>
            <option value="covered">{t('Covered', 'Cubierto', lang)}</option>
            <option value="partial">{t('Partial', 'Parcial', lang)}</option>
            <option value="gap">{t('Gap', 'Brecha', lang)}</option>
            <option value="uncovered">{t('Uncovered', 'Sin cobertura', lang)}</option>
          </select>
          <select className="form-select form-select-sm" style={{ maxWidth: 160 }}
            value={filterDeprecated} onChange={e => setFilterDeprecated(e.target.value)}>
            <option value="">{t('All Clauses', 'Todas las cláusulas', lang)}</option>
            <option value="active">{t('Active only', 'Solo activas', lang)}</option>
            <option value="deprecated">{t('⚠️ Deprecated', '⚠️ Obsoletas', lang)}</option>
          </select>
          {stats.notApplicable > 0 && (
            <select className="form-select form-select-sm" style={{ maxWidth: 160 }}
              value={filterApplicability} onChange={e => setFilterApplicability(e.target.value)}>
              <option value="">{t('All applicability', 'Toda aplicabilidad', lang)}</option>
              <option value="hide_na">{t('Hide N/A', 'Ocultar N/A', lang)}</option>
              <option value="na_only">{t('N/A only', 'Solo N/A', lang)}</option>
            </select>
          )}
          {(filterCat || filterCoverage || filterSearch || filterDeprecated || filterApplicability) && (
            <button className="btn btn-outline-secondary btn-sm"
              onClick={() => { setFilterCat(''); setFilterCoverage(''); setFilterSearch(''); setFilterDeprecated(''); setFilterApplicability(''); }}>
              {t('Clear', 'Limpiar', lang)}
            </button>
          )}
          <span className="text-muted ms-auto align-self-center" style={{ fontSize: 13 }}>
            {filteredClauses.length} / {stats.total} {t('clauses', 'cláusulas', lang)}
          </span>
        </div>
      )}

      {/* Clause table */}
      {stats.total > 0 && (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 90 }}>{t('Clause', 'Cláusula', lang)}</th>
                  <th>{t('Title', 'Título', lang)}</th>
                  <th style={{ width: 110 }}>{t('Category', 'Categoría', lang)}</th>
                  <th style={{ width: 120 }}>{t('Coverage', 'Cobertura', lang)}</th>
                  <th style={{ width: 200 }}>{t('Linked Documents', 'Documentos vinculados', lang)}</th>
                  <th style={{ width: 100 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredClauses.map(clause => {
                  const expanded = expandedId === clause.id;
                  const col = coverageColor(clause);
                  const hasOrphanedLinks = clause.deprecated && (clause.coverageLinks.length > 0 || (clause.attachments || []).length > 0);
                  const isNA = clause.applicabilityStatus === 'not_applicable';
                  const isConditional = clause.applicabilityStatus === 'conditional';
                  return (
                    <React.Fragment key={clause.id}>
                      <tr
                        className={`align-middle ${clause.deprecated ? 'table-secondary' : isNA ? '' : col === 'danger' ? 'table-danger' : col === 'warning' ? 'table-warning' : col === 'secondary' ? 'table-light' : ''}`}
                        style={{ cursor: 'pointer', opacity: clause.deprecated ? 0.75 : isNA ? 0.45 : 1 }}
                        onClick={() => setExpandedId(expanded ? null : clause.id)}
                      >
                        <td>
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <span className="fw-semibold" style={{ fontSize: 13, textDecoration: clause.deprecated ? 'line-through' : 'none' }}>{clause.clauseRef}</span>
                            {clause.deprecated && (
                              <span className={`badge bg-${hasOrphanedLinks ? 'warning text-dark' : 'secondary'}`} style={{ fontSize: 10 }}>
                                {hasOrphanedLinks ? '⚠️ ' : ''}{t('Deprecated', 'Obsoleta', lang)}
                              </span>
                            )}
                            {isNA && (
                              <span className="badge bg-dark" style={{ fontSize: 10 }} title={clause.applicabilityNote || ''}>
                                N/A
                              </span>
                            )}
                            {isConditional && (
                              <span className="badge bg-warning text-dark" style={{ fontSize: 10 }} title={clause.applicabilityNote || ''}>
                                {t('Conditional', 'Condicional', lang)}
                              </span>
                            )}
                          </div>
                          {clause.elementNumber && (
                            <div className="text-muted" style={{ fontSize: 11 }}>{clause.elementNumber}</div>
                          )}
                        </td>
                        <td style={{ fontSize: 13 }}>
                          {clause.title || <span className="text-muted">—</span>}
                        </td>
                        <td>
                          <span className={`badge bg-${CATEGORY_COLORS[clause.category] || 'secondary'}`} style={{ fontSize: 10 }}>
                            {CATEGORY_LABELS[clause.category] || clause.category}
                          </span>
                        </td>
                        <td>
                          <span className={`badge bg-${col}`} style={{ fontSize: 11 }}>
                            {coverageLabel(clause, lang)}
                          </span>
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {clause.coverageLinks.length === 0 && (clause.attachments || []).length === 0
                            ? <span className="text-muted">—</span>
                            : <>
                                {clause.coverageLinks.slice(0, 2).map(l => (
                                  <span key={l.id} className="badge bg-light text-dark border me-1" style={{ fontSize: 10 }}>
                                    {l.entityType === 'policy' ? '📄' : l.entityType === 'checklist' ? '✅' : '✋'} {l.entityName?.slice(0, 20)}{l.entityName?.length > 20 ? '…' : ''}
                                  </span>
                                ))}
                                {clause.coverageLinks.length > 2 && (
                                  <span className="text-muted me-1" style={{ fontSize: 11 }}>+{clause.coverageLinks.length - 2}</span>
                                )}
                                {(clause.attachments || []).length > 0 && (
                                  <span className="badge bg-light text-dark border me-1" style={{ fontSize: 10 }} title={`${clause.attachments.length} attachment${clause.attachments.length > 1 ? 's' : ''}`}>
                                    📎 {clause.attachments.length}
                                  </span>
                                )}
                              </>
                          }
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <button
                            className="btn btn-outline-primary btn-sm me-1"
                            style={{ fontSize: 11 }}
                            onClick={() => openLinkModal(clause)}
                            title={t('Link document', 'Vincular documento', lang)}
                          >
                            + {t('Link', 'Vincular', lang)}
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={6} className="bg-light p-3">
                            {clause.deprecated && (
                              <div className={`alert alert-${hasOrphanedLinks ? 'warning' : 'secondary'} py-2 mb-2`} style={{ fontSize: 13 }}>
                                {hasOrphanedLinks
                                  ? t(
                                      '⚠️ This clause was removed in the latest SQF Code import. It still has policies, checklists, or files linked — review and re-link them to the correct active clause, then delete this one.',
                                      '⚠️ Esta cláusula fue eliminada en la última importación. Aún tiene políticas, listas o archivos vinculados — revise y vuelva a vincularlos a la cláusula activa correcta, luego elimine esta.',
                                      lang
                                    )
                                  : t(
                                      'This clause is no longer part of the current SQF Code. No links or attachments are affected. You can safely delete it.',
                                      'Esta cláusula ya no forma parte del código SQF actual. No hay vínculos ni adjuntos afectados. Puede eliminarla.',
                                      lang
                                    )
                                }
                              </div>
                            )}
                            {clause.requirementText && (
                              <p className="mb-2" style={{ fontSize: 13 }}>
                                <strong>{t('Requirement:', 'Requisito:', lang)}</strong> {clause.requirementText}
                              </p>
                            )}
                            {(isNA || isConditional) && (
                              <div className="alert alert-secondary py-2 mb-2 d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: 12 }}>
                                <span className={`badge bg-${isNA ? 'dark' : 'warning text-dark'}`}>
                                  {isNA ? 'N/A' : t('Conditional', 'Condicional', lang)}
                                </span>
                                {clause.applicabilityNote && (
                                  <span className="flex-grow-1 text-muted">{clause.applicabilityNote}</span>
                                )}
                                <button
                                  className="btn btn-outline-secondary btn-sm py-0"
                                  style={{ fontSize: 11 }}
                                  onClick={async e => {
                                    e.stopPropagation();
                                    try {
                                      await sqfService.updateClauseApplicability(clause.id, { applicabilityStatus: 'applicable', applicabilityNote: null });
                                      setHub(prev => !prev ? prev : {
                                        ...prev,
                                        clauses: prev.clauses.map(c => c.id !== clause.id ? c : { ...c, applicabilityStatus: 'applicable', applicabilityNote: null }),
                                      });
                                    } catch { showToast(t('Failed to update applicability.', 'Error al actualizar aplicabilidad.', lang)); }
                                  }}
                                >
                                  {t('Mark as Applicable', 'Marcar como aplicable', lang)}
                                </button>
                              </div>
                            )}
                            {clause.coverageLinks.length > 0 && (
                              <div className="mb-2">
                                <strong style={{ fontSize: 12 }}>{t('Linked:', 'Vinculado:', lang)}</strong>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                  {clause.coverageLinks.map(l => (
                                    <div key={l.id} className="d-flex align-items-center gap-1 border rounded px-2 py-1 bg-white" style={{ fontSize: 12 }}>
                                      <span className={`badge bg-${COVERAGE_CONFIG[l.coverageStatus]?.color || 'secondary'}`} style={{ fontSize: 10 }}>
                                        {COVERAGE_CONFIG[l.coverageStatus]?.label || l.coverageStatus}
                                      </span>
                                      {l.entityType === 'policy' ? '📄' : l.entityType === 'checklist' ? '✅' : '✋'}
                                      <span className="fw-semibold">{l.entityName}</span>
                                      {l.notes && <span className="text-muted ms-1">— {l.notes}</span>}
                                      {l.entityType !== 'manual' && (
                                        <button
                                          className="btn btn-link btn-sm p-0 ms-1 text-primary"
                                          style={{ fontSize: 11, lineHeight: 1 }}
                                          onClick={e => { e.stopPropagation(); handleViewDocument(l.entityType, l.entityId, l.entityName); }}
                                          title={t('View document', 'Ver documento', lang)}
                                        >
                                          {t('View', 'Ver', lang)}
                                        </button>
                                      )}
                                      <button
                                        className="btn btn-link btn-sm text-danger p-0 ms-1"
                                        style={{ fontSize: 11, lineHeight: 1 }}
                                        onClick={e => { e.stopPropagation(); handleRemoveLink(l.id); }}
                                        title={t('Remove link', 'Eliminar vínculo', lang)}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* Attachments */}
                            <div className="mt-2">
                              <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                <strong style={{ fontSize: 12 }}>{t('Attachments:', 'Adjuntos:', lang)}</strong>
                                <select
                                  className="form-select form-select-sm"
                                  style={{ width: 'auto', fontSize: 11 }}
                                  value={attachType}
                                  onChange={e => setAttachType(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <option value="certificate">{t('Certificate', 'Certificado', lang)}</option>
                                  <option value="audit_report">{t('Audit Report', 'Informe de auditoría', lang)}</option>
                                  <option value="lab_result">{t('Lab Result', 'Resultado de laboratorio', lang)}</option>
                                  <option value="procedure">{t('Procedure', 'Procedimiento', lang)}</option>
                                  <option value="other">{t('Other', 'Otro', lang)}</option>
                                </select>
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  style={{ fontSize: 11 }}
                                  disabled={attachUploading && attachClauseId === clause.id}
                                  onClick={e => { e.stopPropagation(); openAttachPicker(clause.id); }}
                                >
                                  {attachUploading && attachClauseId === clause.id
                                    ? <><span className="spinner-border spinner-border-sm me-1" />{t('Uploading…', 'Subiendo…', lang)}</>
                                    : t('+ Upload File', '+ Subir archivo', lang)}
                                </button>
                              </div>
                              {(clause.attachments || []).length > 0 && (
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                  {(clause.attachments || []).map(a => {
                                    const ATTACH_ICON = { certificate: '🏆', audit_report: '📋', lab_result: '🔬', procedure: '📝', other: '📎' };
                                    const ATTACH_LABEL = { certificate: 'Certificate', audit_report: 'Audit Report', lab_result: 'Lab Result', procedure: 'Procedure', other: 'Other' };
                                    const isPdf = a.mimeType === 'application/pdf' || a.fileName?.toLowerCase().endsWith('.pdf');
                                    const isImage = a.mimeType?.startsWith('image/');
                                    return (
                                      <div key={a.id} className="d-flex align-items-center gap-1 border rounded px-2 py-1 bg-white" style={{ fontSize: 12 }}>
                                        <span title={ATTACH_LABEL[a.attachmentType] || a.attachmentType}>{ATTACH_ICON[a.attachmentType] || '📎'}</span>
                                        <span className="fw-semibold" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.fileName}>{a.fileName}</span>
                                        {isPdf && (
                                          <a href={a.fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-link btn-sm p-0 ms-1" style={{ fontSize: 11 }} onClick={e => e.stopPropagation()}>
                                            {t('View', 'Ver', lang)}
                                          </a>
                                        )}
                                        {isImage && (
                                          <a href={a.fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-link btn-sm p-0 ms-1" style={{ fontSize: 11 }} onClick={e => e.stopPropagation()}>
                                            {t('View', 'Ver', lang)}
                                          </a>
                                        )}
                                        <a href={a.fileUrl} download={a.fileName} className="btn btn-link btn-sm p-0 ms-1 text-secondary" style={{ fontSize: 11 }} onClick={e => e.stopPropagation()}>
                                          ↓
                                        </a>
                                        <button
                                          className="btn btn-link btn-sm text-danger p-0 ms-1"
                                          style={{ fontSize: 11, lineHeight: 1 }}
                                          onClick={e => { e.stopPropagation(); handleDeleteAttachment(a.id, clause.id); }}
                                        >×</button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            <button
                              className="btn btn-outline-danger btn-sm mt-2"
                              style={{ fontSize: 11 }}
                              onClick={e => { e.stopPropagation(); handleDeleteClause(clause.id); }}
                            >
                              {t('Delete Clause', 'Eliminar cláusula', lang)}
                            </button>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {filteredClauses.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      {t('No clauses match the current filters.', 'Ninguna cláusula coincide con los filtros actuales.', lang)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Link Modal ──────────────────────────────────────────────────────── */}
      {linkModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t('Link Document to Clause', 'Vincular documento a cláusula', lang)}{' '}
                  <span className="text-muted fw-normal">{linkModal.clause.clauseRef}</span>
                </h5>
                <button type="button" className="btn-close" onClick={() => setLinkModal(null)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Document Type', 'Tipo de documento', lang)}</label>
                  <div className="btn-group w-100">
                    <button
                      className={`btn btn-sm ${linkEntityType === 'policy' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => { setLinkEntityType('policy'); setLinkEntityId(''); setLinkSearch(''); setLinkDropdownOpen(false); }}
                    >
                      📄 {t('Policy', 'Política', lang)}
                    </button>
                    <button
                      className={`btn btn-sm ${linkEntityType === 'checklist' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => { setLinkEntityType('checklist'); setLinkEntityId(''); setLinkSearch(''); setLinkDropdownOpen(false); }}
                    >
                      ✅ {t('Checklist', 'Lista de verificación', lang)}
                    </button>
                    <button
                      className={`btn btn-sm ${linkEntityType === 'manual' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                      onClick={() => { setLinkEntityType('manual'); setLinkEntityId(''); setLinkSearch(''); setLinkDropdownOpen(false); }}
                    >
                      ✋ {t('Manual', 'Manual', lang)}
                    </button>
                  </div>
                </div>
                {linkEntityType !== 'manual' ? (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      {linkEntityType === 'policy' ? t('Select Policy', 'Seleccionar política', lang) : t('Select Checklist', 'Seleccionar lista', lang)}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={t('Type to search…', 'Escriba para buscar…', lang)}
                        value={linkSearch}
                        onChange={e => { setLinkSearch(e.target.value); setLinkEntityId(''); setLinkDropdownOpen(true); }}
                        onFocus={() => setLinkDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setLinkDropdownOpen(false), 150)}
                        autoComplete="off"
                      />
                      {linkDropdownOpen && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1060, maxHeight: 200, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0 0 4px 4px', background: '#fff', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
                          {filteredEntityOptions.length === 0 ? (
                            <div style={{ padding: '8px 12px', color: '#999', fontSize: 13 }}>
                              {t('No results', 'Sin resultados', lang)}
                            </div>
                          ) : filteredEntityOptions.map(opt => (
                            <div
                              key={opt.id}
                              style={{ padding: '7px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f0f0f0' }}
                              onMouseDown={() => {
                                setLinkEntityId(String(opt.id));
                                setLinkSearch(opt.name + (!opt.activeVersionId ? ` (${t('draft', 'borrador', lang)})` : ''));
                                setLinkDropdownOpen(false);
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#f8f9fa'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                            >
                              {opt.name}
                              {!opt.activeVersionId && (
                                <span className="text-muted ms-1" style={{ fontSize: 11 }}>({t('draft', 'borrador', lang)})</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Upload new document directly from this modal */}
                    <div className="mt-2 d-flex align-items-center gap-2">
                      <span className="text-muted" style={{ fontSize: 12 }}>{t('or', 'o', lang)}</span>
                      <input
                        ref={linkUploadRef}
                        type="file"
                        accept=".pdf,.docx,.doc"
                        style={{ display: 'none' }}
                        onChange={handleLinkUpload}
                      />
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        disabled={linkUploading}
                        onClick={() => { setLinkUploadMsg(''); linkUploadRef.current?.click(); }}
                      >
                        {linkUploading
                          ? <><span className="spinner-border spinner-border-sm me-1" />{t('Importing…', 'Importando…', lang)}</>
                          : t('Upload & import document', 'Subir e importar documento', lang)}
                      </button>
                    </div>
                    {linkUploadMsg && (
                      <div className={`alert ${linkUploadMsg.startsWith('Error') ? 'alert-danger' : 'alert-info'} py-2 mt-2 mb-0`} style={{ fontSize: 12 }}>
                        {linkUploadMsg}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-3">
                    <div className="alert alert-secondary py-2 mb-0" style={{ fontSize: 13 }}>
                      ✋ {t('Mark this clause as covered without linking a specific document — use this when compliance is established through general operations, practices, or physical evidence not captured in the system.', 'Marcar esta cláusula como cubierta sin vincular un documento específico — use esto cuando el cumplimiento se establece a través de operaciones generales, prácticas o evidencia física no capturada en el sistema.', lang)}
                    </div>
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Coverage Status', 'Estado de cobertura', lang)}</label>
                  <div className="btn-group w-100">
                    {Object.entries(COVERAGE_CONFIG).map(([k, v]) => (
                      <button
                        key={k}
                        className={`btn btn-sm ${linkStatus === k ? `btn-${v.color}` : `btn-outline-${v.color}`}`}
                        onClick={() => setLinkStatus(k)}
                      >
                        {t(v.label, v.label, lang)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Notes (optional)', 'Notas (opcional)', lang)}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={linkNotes}
                    onChange={e => setLinkNotes(e.target.value)}
                    placeholder={t('e.g. Section 3.2 addresses this requirement', 'ej. La sección 3.2 aborda este requisito', lang)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-dark btn-sm me-auto"
                  disabled={linkMarkingNA}
                  onClick={async () => {
                    setLinkMarkingNA(true);
                    try {
                      await sqfService.updateClauseApplicability(linkModal.clause.id, { applicabilityStatus: 'not_applicable', applicabilityNote: null });
                      setHub(prev => !prev ? prev : {
                        ...prev,
                        clauses: prev.clauses.map(c => c.id !== linkModal.clause.id ? c : { ...c, applicabilityStatus: 'not_applicable', applicabilityNote: null }),
                      });
                      setLinkModal(null);
                    } catch { alert(t('Failed to update applicability.', 'Error al actualizar aplicabilidad.', lang)); }
                    finally { setLinkMarkingNA(false); }
                  }}
                >
                  {linkMarkingNA
                    ? <><span className="spinner-border spinner-border-sm me-1" />{t('Saving…', 'Guardando…', lang)}</>
                    : t('Mark as N/A', 'Marcar como N/A', lang)}
                </button>
                <button className="btn btn-secondary" onClick={() => setLinkModal(null)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={(linkEntityType !== 'manual' && !linkEntityId) || linkSaving}
                  onClick={handleSaveLink}
                >
                  {linkSaving
                    ? <><span className="spinner-border spinner-border-sm me-1" />{t('Saving…', 'Guardando…', lang)}</>
                    : t('Save Link', 'Guardar vínculo', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── View Document Modal ─────────────────────────────────────────────── */}
      {viewModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {viewModal.type === 'policy' ? '📄' : '✅'}{' '}
                  {viewModal.type === 'policy'
                    ? (viewLang === 'es' && viewModal.policy?.nameEs ? viewModal.policy.nameEs : viewModal.policy?.name)
                    : viewModal.name}
                </h5>
                <div className="d-flex gap-2 ms-auto me-2">
                  {viewModal.type === 'policy' && viewModal.policy && (
                    <Link
                      to={`/sqf/policies/${viewModal.policy.id}/edit`}
                      className="btn btn-outline-primary btn-sm"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ↗ {t('Open Editor', 'Abrir editor', lang)}
                    </Link>
                  )}
                  {viewModal.type === 'checklist' && viewModal.templateId && (
                    <Link
                      to={`/sqf/templates/${viewModal.templateId}/edit`}
                      className="btn btn-outline-primary btn-sm"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ↗ {t('Open Editor', 'Abrir editor', lang)}
                    </Link>
                  )}
                </div>
                <button type="button" className="btn-close" onClick={() => setViewModal(null)} />
              </div>
              <div className="modal-body">
                {viewModal.loading ? (
                  <div className="text-center py-5 text-muted">
                    <div className="spinner-border spinner-border-sm me-2" />
                    {t('Loading…', 'Cargando…', lang)}
                  </div>
                ) : viewModal.type === 'policy' ? (
                  <PolicyDocumentView
                    policy={viewModal.policy}
                    version={viewModal.version}
                    lang={lang}
                    viewLang={viewLang}
                    onViewLangChange={setViewLang}
                    allVersions={(viewModal.policy?.Versions || [])
                      .filter(v => v.status === 'published')
                      .sort((a, b) => b.versionNumber - a.versionNumber)}
                  />
                ) : (
                  // Checklist schema view
                  <div>
                    {(viewModal.schemaJson?.sections || []).length === 0
                      ? <p className="text-muted">{t('No sections found in this checklist.', 'No se encontraron secciones en esta lista.', lang)}</p>
                      : (viewModal.schemaJson.sections || []).map((section, si) => (
                          <div key={si} className="mb-4">
                            <h6 className="fw-bold border-bottom pb-1 mb-2">{section.title || `Section ${si + 1}`}</h6>
                            {(section.fields || []).map((field, fi) => (
                              <div key={fi} className="d-flex align-items-start gap-2 mb-1 ps-2" style={{ fontSize: 13 }}>
                                <span className="badge bg-secondary" style={{ fontSize: 10, minWidth: 60 }}>{field.type || 'text'}</span>
                                <span>{field.label || field.id}</span>
                                {field.required && <span className="text-danger" style={{ fontSize: 11 }}>*</span>}
                              </div>
                            ))}
                          </div>
                        ))
                    }
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setViewModal(null)}>
                  {t('Close', 'Cerrar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Pre-Import Modal (mode + facility profile) ─────────────────────── */}
      {preImportModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📥 {t('Import SQF Code PDF', 'Importar PDF del código SQF', lang)}</h5>
                <button type="button" className="btn-close" onClick={() => { setPreImportModal(false); setPendingFile(null); setPendingImportMode(null); }} />
              </div>
              <div className="modal-body">
                {hub?.stats?.total > 0 && (
                  <>
                    <p className="text-muted mb-2" style={{ fontSize: 14 }}>
                      {t('You already have clauses imported. How should this import be handled?', 'Ya tiene cláusulas importadas. ¿Cómo debe manejarse esta importación?', lang)}
                    </p>
                    <div className="d-grid gap-2 mb-4">
                      <button
                        className={`btn text-start p-3 ${pendingImportMode === 'replace' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setPendingImportMode('replace')}
                      >
                        <div className="fw-semibold">🔄 {t('New Edition / Full Replace', 'Nueva edición / Reemplazo completo', lang)}</div>
                        <div className={`mt-1 ${pendingImportMode === 'replace' ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: 13 }}>
                          {t('Update all existing clauses, add new ones, and mark any clauses no longer in the PDF as deprecated.', 'Actualiza todas las cláusulas, agrega nuevas y marca las que ya no están en el PDF como obsoletas.', lang)}
                        </div>
                      </button>
                      <button
                        className={`btn text-start p-3 ${pendingImportMode === 'add' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        onClick={() => setPendingImportMode('add')}
                      >
                        <div className="fw-semibold">➕ {t('Add / Update Only', 'Solo agregar / actualizar', lang)}</div>
                        <div className={`mt-1 ${pendingImportMode === 'add' ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: 13 }}>
                          {t('Add new clauses and update existing ones, but never deprecate anything. Use this to fill gaps from a partial import.', 'Agrega nuevas cláusulas y actualiza las existentes, sin marcar nada como obsoleto. Use esto para completar importaciones parciales.', lang)}
                        </div>
                      </button>
                    </div>
                  </>
                )}
                <div>
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    🏭 {t('Facility Type', 'Tipo de instalación', lang)}
                    <span className="text-muted fw-normal ms-1" style={{ fontSize: 12 }}>
                      {t('(optional — used to auto-mark non-applicable clauses)', '(opcional — para marcar cláusulas no aplicables automáticamente)', lang)}
                    </span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder={t('e.g. Frozen pizza manufacturer operating under FDA and USDA regulations', 'ej. Fabricante de pizza congelada bajo regulaciones FDA y USDA', lang)}
                    value={pendingFacilityProfile}
                    onChange={e => setPendingFacilityProfile(e.target.value)}
                  />
                  {pendingFacilityProfile.trim() && (
                    <div className="form-text text-success mt-1" style={{ fontSize: 12 }}>
                      ✅ {t('After import, AI will automatically mark non-applicable clauses for your facility type.', 'Después de importar, la IA marcará automáticamente las cláusulas no aplicables para su tipo de instalación.', lang)}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setPreImportModal(false); setPendingFile(null); setPendingImportMode(null); }}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={hub?.stats?.total > 0 && !pendingImportMode}
                  onClick={handleConfirmImport}
                >
                  {t('Start Import', 'Iniciar importación', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Import Report Modal ─────────────────────────────────────────────── */}
      {reportModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📊 {t('Import Report', 'Informe de importación', lang)}</h5>
                <button type="button" className="btn-close" onClick={() => setReportModal(null)} />
              </div>
              <div className="modal-body">
                <div className="row g-2 mb-3">
                  {[
                    { label: t('New',        'Nuevas',        lang), value: reportModal.created,     color: 'success'   },
                    { label: t('Updated',    'Actualizadas',  lang), value: reportModal.updated,     color: 'primary'   },
                    { label: t('Deprecated', 'Obsoletas',     lang), value: reportModal.deprecated,  color: 'warning'   },
                    { label: t('Skipped',    'Omitidas',      lang), value: reportModal.skippedSections?.length || 0, color: reportModal.skippedSections?.length ? 'danger' : 'secondary' },
                  ].map(s => (
                    <div key={s.label} className="col-3">
                      <div className={`card text-center border-0 bg-${s.color}-subtle`}>
                        <div className="card-body py-2 px-1">
                          <div style={{ fontSize: 22, fontWeight: 700 }}>{s.value}</div>
                          <div className="text-muted" style={{ fontSize: 11 }}>{s.label}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                  {t(`${reportModal.totalSections} total sections processed.`, `${reportModal.totalSections} secciones procesadas en total.`, lang)}
                </div>
                {reportModal.skippedSections?.length > 0 ? (
                  <div className="alert alert-warning py-2" style={{ fontSize: 13 }}>
                    <strong>⚠️ {t('Sections skipped:', 'Secciones omitidas:', lang)}</strong>{' '}
                    {reportModal.skippedSections.join(', ')}
                    <div className="mt-1">
                      {t(
                        'These sections could not be processed after 3 attempts. The clauses they contain may be missing. Use "Add / Update Only" mode to re-import and fill the gaps, or add the missing clauses manually.',
                        'Estas secciones no pudieron procesarse después de 3 intentos. Las cláusulas que contienen pueden faltar. Use el modo "Solo agregar / actualizar" para volver a importar y completar los vacíos, o agregue las cláusulas faltantes manualmente.',
                        lang
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-success py-2" style={{ fontSize: 13 }}>
                    ✅ {t('All sections were processed successfully. Your clause library is complete.', 'Todas las secciones se procesaron correctamente. Su biblioteca de cláusulas está completa.', lang)}
                  </div>
                )}
                {reportModal.applicabilityStats && (
                  <div className="mt-3">
                    <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>🏭 {t('Applicability Assessment', 'Evaluación de aplicabilidad', lang)}</div>
                    <div className="row g-2">
                      {[
                        { label: t('Applicable',     'Aplicable',     lang), value: reportModal.applicabilityStats.applicable,     color: 'success'  },
                        { label: t('Conditional',    'Condicional',   lang), value: reportModal.applicabilityStats.conditional,    color: 'warning'  },
                        { label: t('Not Applicable', 'No aplicable',  lang), value: reportModal.applicabilityStats.not_applicable, color: 'secondary' },
                      ].map(s => (
                        <div key={s.label} className="col-4">
                          <div className={`card text-center border-0 bg-${s.color}-subtle`}>
                            <div className="card-body py-2 px-1">
                              <div style={{ fontSize: 20, fontWeight: 700 }}>{s.value ?? '—'}</div>
                              <div className="text-muted" style={{ fontSize: 11 }}>{s.label}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {reportModal.skippedSections?.length > 0 && (
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => { setReportModal(null); setAddClauseForm({ clauseRef: '', elementNumber: '', title: '', requirementText: '', category: 'other' }); setAddClauseError(''); setAddClauseModal(true); }}
                  >
                    + {t('Add Missing Clause Manually', 'Agregar cláusula faltante manualmente', lang)}
                  </button>
                )}
                <button className="btn btn-primary" onClick={() => setReportModal(null)}>
                  {t('Close', 'Cerrar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Add Clause Modal ────────────────────────────────────────────────── */}
      {addClauseModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">+ {t('Add Clause Manually', 'Agregar cláusula manualmente', lang)}</h5>
                <button type="button" className="btn-close" onClick={() => setAddClauseModal(false)} />
              </div>
              <div className="modal-body">
                {addClauseError && (
                  <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{addClauseError}</div>
                )}
                <div className="row g-2 mb-2">
                  <div className="col-6">
                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Clause Ref', 'Referencia', lang)} <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="e.g. 2.1.1"
                      value={addClauseForm.clauseRef}
                      onChange={e => setAddClauseForm(f => ({ ...f, clauseRef: e.target.value }))}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Element Number', 'Número de elemento', lang)}</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="e.g. 2"
                      value={addClauseForm.elementNumber}
                      onChange={e => setAddClauseForm(f => ({ ...f, elementNumber: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Title', 'Título', lang)}</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t('Clause title', 'Título de la cláusula', lang)}
                    value={addClauseForm.title}
                    onChange={e => setAddClauseForm(f => ({ ...f, title: e.target.value }))}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Requirement Text', 'Texto del requisito', lang)}</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={4}
                    placeholder={t('The full requirement/shall statement…', 'El requisito completo…', lang)}
                    value={addClauseForm.requirementText}
                    onChange={e => setAddClauseForm(f => ({ ...f, requirementText: e.target.value }))}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>{t('Category', 'Categoría', lang)}</label>
                  <select
                    className="form-select form-select-sm"
                    value={addClauseForm.category}
                    onChange={e => setAddClauseForm(f => ({ ...f, category: e.target.value }))}
                  >
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setAddClauseModal(false)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!addClauseForm.clauseRef.trim() || addClauseSaving}
                  onClick={handleAddClause}
                >
                  {addClauseSaving
                    ? <><span className="spinner-border spinner-border-sm me-1" />{t('Saving…', 'Guardando…', lang)}</>
                    : t('Add Clause', 'Agregar cláusula', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── AI Clause Chat Modal ────────────────────────────────────────────── */}
      {chatModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🤖 {t('Ask AI about SQF Clauses', 'Preguntar a IA sobre cláusulas SQF', lang)}</h5>
                <button type="button" className="btn-close" onClick={() => setChatModal(false)} />
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                  {t(
                    'Ask questions like "Which clause covers senior management commitment?" or "What procedures do I need for clause 2.1.1?"',
                    'Haga preguntas como "¿Qué cláusula cubre el compromiso de la alta dirección?" o "¿Qué procedimientos necesito para la cláusula 2.1.1?"',
                    lang
                  )}
                </p>
                <div className="input-group mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('Ask a question about SQF clauses…', 'Haga una pregunta sobre las cláusulas SQF…', lang)}
                    value={chatQuestion}
                    onChange={e => setChatQuestion(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleChat()}
                    disabled={chatLoading}
                  />
                  <button
                    className="btn btn-primary"
                    disabled={!chatQuestion.trim() || chatLoading}
                    onClick={handleChat}
                  >
                    {chatLoading
                      ? <span className="spinner-border spinner-border-sm" />
                      : t('Ask', 'Preguntar', lang)}
                  </button>
                </div>
                {chatError && (
                  <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{chatError}</div>
                )}
                {chatAnswer && (
                  <div className="card border-0 bg-light">
                    <div className="card-body py-3" style={{ fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {chatAnswer}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setChatModal(false)}>
                  {t('Close', 'Cerrar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Edit Facility Profile Modal ─────────────────────────────────────── */}
      {editProfileModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🏭 {t('Facility Type', 'Tipo de instalación', lang)}</h5>
                <button type="button" className="btn-close" onClick={() => setEditProfileModal(false)} />
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                  {t(
                    'Describe your facility type and regulatory scope. This allows AI to automatically identify clauses that do not apply to your operation.',
                    'Describa el tipo de instalación y su alcance regulatorio. Esto permite a la IA identificar automáticamente las cláusulas que no aplican a su operación.',
                    lang
                  )}
                </p>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder={t('e.g. Frozen pizza manufacturer operating under FDA and USDA regulations, SQF Edition 9 Food Safety', 'ej. Fabricante de pizza congelada bajo regulaciones FDA y USDA, Inocuidad Alimentaria SQF Edición 9', lang)}
                  value={editProfileValue}
                  onChange={e => setEditProfileValue(e.target.value)}
                />
                {editProfileValue.trim() && facilityProfile !== editProfileValue.trim() && (
                  <div className="form-text text-primary mt-1" style={{ fontSize: 12 }}>
                    💡 {t('After saving, use "Re-assess N/A" to update clause applicability with the new profile.', 'Después de guardar, use "Re-evaluar N/A" para actualizar la aplicabilidad con el nuevo perfil.', lang)}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setEditProfileModal(false)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={editProfileSaving}
                  onClick={handleSaveProfile}
                >
                  {editProfileSaving
                    ? <><span className="spinner-border spinner-border-sm me-1" />{t('Saving…', 'Guardando…', lang)}</>
                    : t('Save', 'Guardar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Clear All Confirm ───────────────────────────────────────────────── */}
      {showClearConfirm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">{t('Clear All Clauses', 'Eliminar todas las cláusulas', lang)}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowClearConfirm(false)} />
              </div>
              <div className="modal-body">
                <p>
                  {t(
                    'This will permanently delete all imported SQF Code clauses and their coverage links. This action cannot be undone.',
                    'Esto eliminará permanentemente todas las cláusulas del código SQF importadas y sus vínculos de cobertura. Esta acción no se puede deshacer.',
                    lang
                  )}
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowClearConfirm(false)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button className="btn btn-danger" disabled={clearing} onClick={handleClearAll}>
                  {clearing
                    ? <><span className="spinner-border spinner-border-sm me-1" />{t('Clearing…', 'Eliminando…', lang)}</>
                    : t('Yes, Clear All', 'Sí, eliminar todo', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showVisualInspectionModal && (
        <StartVisualInspectionModal onClose={() => setShowVisualInspectionModal(false)} />
      )}
    </div>
    </>
  );
}
